import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Network, Plus, Search, Building2, Truck, Warehouse, Anchor, Briefcase, Trash2, Pencil, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "SUPPLIER", label: "Supplier / Shipper", icon: Building2, color: "#002FA7" },
  { value: "CONSIGNEE", label: "Consignee / Receiver", icon: Building2, color: "#166534" },
  { value: "NOTIFY", label: "Notify Party", icon: Mail, color: "#92400E" },
  { value: "CUSTOMS_BROKER", label: "Customs Broker", icon: Briefcase, color: "#7C3AED" },
  { value: "FORWARDER", label: "Freight Forwarder", icon: Anchor, color: "#0891B2" },
  { value: "TRUCKING", label: "Trucking / Cartage", icon: Truck, color: "#B45309" },
  { value: "WAREHOUSE", label: "Warehouse / 3PL", icon: Warehouse, color: "#6B7280" },
];

export default function StakeholderNetwork() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ role: "ALL", q: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const params = {};
    if (filter.role && filter.role !== "ALL") params.role = filter.role;
    const r = await api.get("/network/stakeholders", { params });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [filter.role]);

  const filtered = useMemo(() => {
    if (!filter.q) return rows;
    const q = filter.q.toLowerCase();
    return rows.filter((r) =>
      [r.name, r.country, r.city, r.contact_name, r.contact_email].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [rows, filter.q]);

  const countByRole = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.role] = (m[r.role] || 0) + 1; });
    return m;
  }, [rows]);

  const remove = async (id) => {
    if (!window.confirm("Remove this stakeholder?")) return;
    await api.delete(`/network/stakeholders/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="network-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Stakeholder Network</h1>
          <p className="text-sm text-zinc-500 mt-1">Centralise every supplier, consignee, broker, and partner in one address book. Reuse them across bookings, RFQs, and shipments.</p>
        </div>
        <Button data-testid="net-add" onClick={() => { setEditing(null); setDialogOpen(true); }} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">
          <Plus className="w-4 h-4 mr-2" />Add stakeholder
        </Button>
      </div>

      {/* Role summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-zinc-200 border border-zinc-200">
        {ROLES.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.value}
              data-testid={`net-stat-${r.value}`}
              onClick={() => setFilter({ ...filter, role: filter.role === r.value ? "ALL" : r.value })}
              className={`bg-white p-4 text-left hover:bg-zinc-50/60 transition-colors border-l-[3px] ${filter.role === r.value ? "border-l-[#002FA7] bg-[#E6EBF6]/60" : "border-l-transparent"}`}
            >
              <div className="data-label flex items-center gap-1.5"><Icon className="w-3 h-3" style={{ color: r.color }} />{r.label.split(" / ")[0]}</div>
              <div className="metric text-2xl mt-1">{countByRole[r.value] || 0}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 p-4 flex items-center gap-3 flex-wrap">
        <Search className="w-4 h-4 text-zinc-400" />
        <Input
          data-testid="net-search"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          placeholder="Search name, country, contact..."
          className="rounded-none h-9 flex-1 min-w-[240px]"
        />
        <Select value={filter.role} onValueChange={(v) => setFilter({ ...filter, role: v })}>
          <SelectTrigger data-testid="net-role-filter" className="rounded-none h-9 w-48"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="ALL">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500 metric ml-auto">{filtered.length} of {rows.length}</span>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">{["Name", "Role", "Location", "Contact", "Notes", ""].map((h) => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}</tr>
          </thead>
          <tbody data-testid="net-tbody">
            {filtered.map((s) => {
              const role = ROLES.find((r) => r.value === s.role) || ROLES[0];
              const Icon = role.icon;
              return (
                <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`net-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-heading font-semibold">{s.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border inline-flex items-center gap-1" style={{ background: `${role.color}15`, color: role.color, borderColor: `${role.color}40` }}>
                      <Icon className="w-2.5 h-2.5" />{role.label.split(" / ")[0]}
                    </span>
                  </td>
                  <td className="px-4 py-3 metric text-xs text-zinc-600">{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {s.contact_name && <div className="font-medium">{s.contact_name}</div>}
                    {s.contact_email && <div className="metric text-zinc-500 flex items-center gap-1"><Mail className="w-3 h-3" />{s.contact_email}</div>}
                    {s.contact_phone && <div className="metric text-zinc-500 flex items-center gap-1"><Phone className="w-3 h-3" />{s.contact_phone}</div>}
                    {!s.contact_name && !s.contact_email && !s.contact_phone && <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600 max-w-xs truncate" title={s.notes || ""}>{s.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" className="rounded-none h-7" data-testid={`net-edit-${s.id}`} onClick={() => { setEditing(s); setDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 text-[#991B1B]" data-testid={`net-del-${s.id}`} onClick={() => remove(s.id)}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-sm text-zinc-500">
                <Network className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                <div>No stakeholders yet. Click <span className="font-semibold text-zinc-700">Add stakeholder</span> to start building your supply-chain address book.</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <StakeholderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        onSaved={() => { setDialogOpen(false); load(); }}
      />
    </div>
  );
}

function StakeholderDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", role: "SUPPLIER", country: "", city: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      name: editing.name || "", role: editing.role || "SUPPLIER",
      country: editing.country || "", city: editing.city || "",
      contact_name: editing.contact_name || "", contact_email: editing.contact_email || "",
      contact_phone: editing.contact_phone || "", notes: editing.notes || "",
    });
    else setForm({ name: "", role: "SUPPLIER", country: "", city: "", contact_name: "", contact_email: "", contact_phone: "", notes: "" });
  }, [editing, open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (editing) await api.patch(`/network/stakeholders/${editing.id}`, form);
      else await api.post("/network/stakeholders", form);
      toast.success(editing ? "Stakeholder updated" : "Stakeholder added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit stakeholder" : "Add stakeholder"}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Suppliers, consignees, brokers and partners — reusable across the platform.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="data-label">Name *</Label><Input data-testid="net-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1.5 h-10" /></div>
          <div>
            <Label className="data-label">Role *</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="net-form-role" className="rounded-none mt-1.5 h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="data-label">Country</Label><Input data-testid="net-form-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="China" className="rounded-none mt-1.5 h-10" /></div>
          <div><Label className="data-label">City</Label><Input data-testid="net-form-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Shanghai" className="rounded-none mt-1.5 h-10" /></div>
          <div><Label className="data-label">Contact name</Label><Input data-testid="net-form-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none mt-1.5 h-10" /></div>
          <div><Label className="data-label">Email</Label><Input data-testid="net-form-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none mt-1.5 h-10 metric" /></div>
          <div><Label className="data-label">Phone</Label><Input data-testid="net-form-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-none mt-1.5 h-10 metric" /></div>
          <div className="col-span-2"><Label className="data-label">Notes</Label><Input data-testid="net-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Preferred Incoterm, payment terms, etc." className="rounded-none mt-1.5 h-10" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button data-testid="net-form-save" onClick={save} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]">{busy ? "Saving..." : editing ? "Save changes" : "Add stakeholder"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
